import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import Navbar from '../components/Navbar';
import StatCard from '../components/StatCard';
import UnfulfilledPanel from '../components/UnfulfilledPanel';

// Single-hue magnitude colour (one series, so no categorical palette needed)
const BAR = '#38bdf8';
const SURFACE = 'rgba(15,23,42,0.92)';
const panel = { background: SURFACE, borderRadius: 16, border: '1px solid rgba(56,189,248,0.15)', padding: 24 };

const kes = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { maximumFractionDigits: 0 })}`;
const compact = (n) => (n >= 1e6 ? `${+(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${+(n / 1e3).toFixed(1)}K` : `${n}`);
const monthLabel = (ym, style = 'long') =>
  new Date(`${ym}-01T12:00:00`).toLocaleDateString('en-KE', { month: style, year: style === 'long' ? 'numeric' : undefined });
const shiftMonth = (ym, delta) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const nairobiMonth = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Nairobi', year: 'numeric', month: '2-digit' }).format(new Date()).slice(0, 7);

// Ranked horizontal bars (HTML) — value at the bar tip, payments count as secondary text
function RankedBars({ title, rows, emptyText }) {
  const [showAll, setShowAll] = useState(false);
  const max = Math.max(...rows.map((r) => r.total), 0);
  const total = rows.reduce((s, r) => s + r.total, 0);
  const shown = showAll ? rows : rows.slice(0, 8);

  return (
    <div style={panel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 18 }}>
        <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: 0 }}>{title}</h3>
        <span style={{ color: '#cbd5e1', fontSize: 13.5 }}>{rows.length} {rows.length === 1 ? 'item' : 'items'}</span>
      </div>
      {rows.length === 0 && <p style={{ color: '#cbd5e1', fontSize: 15, margin: 0 }}>{emptyText}</p>}
      {shown.map((r) => {
        const pct = max ? (r.total / max) * 100 : 0;
        const share = total ? Math.round((r.total / total) * 100) : 0;
        return (
          <div key={`${r.id}-${r.name}`} title={`${r.name}: ${kes(r.total)} · ${r.count} payment${r.count === 1 ? '' : 's'} · ${share}% of total`}
            style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
              <span style={{ color: r.id === null ? '#e2e8f0' : '#e2e8f0', fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              <span style={{ color: '#e2e8f0', fontSize: 14.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {kes(r.total)} <span style={{ color: '#cbd5e1', fontWeight: 400 }}>· {r.count}</span>
              </span>
            </div>
            <div style={{ height: 8, background: 'rgba(148,163,184,0.1)', borderRadius: 4 }}>
              <div style={{ width: `${pct}%`, minWidth: r.total > 0 ? 4 : 0, height: '100%', background: BAR, borderRadius: 4, transition: 'width 0.4s ease' }} />
            </div>
          </div>
        );
      })}
      {rows.length > 8 && (
        <button onClick={() => setShowAll((v) => !v)} style={{ background: 'none', border: 'none', color: '#38bdf8', fontSize: 14.5, cursor: 'pointer', padding: 0 }}>
          {showAll ? 'Show top 8' : `Show all ${rows.length}`}
        </button>
      )}
    </div>
  );
}

function YtdTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#0f172a', border: '1px solid rgba(56,189,248,0.3)', borderRadius: 8, padding: '8px 12px' }}>
      <div style={{ color: '#e2e8f0', fontSize: 13.5, marginBottom: 4 }}>{monthLabel(d.month)}</div>
      <div style={{ color: '#e2e8f0', fontSize: 15, fontWeight: 700 }}>{kes(d.total)}</div>
      <div style={{ color: '#cbd5e1', fontSize: 13.5 }}>{d.count} payment{d.count === 1 ? '' : 's'}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [month, setMonth] = useState(nairobiMonth());
  const [transacted, setTransacted] = useState(null);
  const [ytd, setYtd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = user.role === 'admin';
  const thisMonth = nairobiMonth();

  const handleError = (err, message) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
    setError(message);
  };

  // Meter stats + year-to-date chart (load once)
  useEffect(() => {
    Promise.all([
      axios.get('/api/dashboard', { headers }),
      axios.get('/api/dashboard/ytd', { headers }),
    ])
      .then(([s, y]) => { setStats(s.data); setYtd(y.data); })
      .catch((err) => handleError(err, 'Failed to load dashboard'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selected month breakdown (reloads when the month changes)
  useEffect(() => {
    setMonthLoading(true);
    axios.get('/api/dashboard/transacted', { headers, params: { month } })
      .then((res) => setTransacted(res.data))
      .catch((err) => handleError(err, 'Failed to load monthly totals'))
      .finally(() => setMonthLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  // Month options: the last 24 months, newest first
  const monthOptions = Array.from({ length: 24 }, (_, i) => shiftMonth(thisMonth, -i));
  const navBtn = (disabled) => ({
    background: disabled ? '#e2e8f0' : '#1a5fa4', border: 'none', borderRadius: 8,
    width: 36, height: 36, color: disabled ? '#94a3b8' : '#ffffff', fontSize: 18, cursor: disabled ? 'default' : 'pointer',
  });

  return (
    <div style={{ minHeight: '100vh' }}>
      <Navbar user={user} />

      <div className="mw-page" style={{ padding: '32px 24px', maxWidth: 1200, margin: '0 auto' }}>
        <style>{`
          @keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>

        {/* Header + month toggle */}
        <div className="mw-page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 28, animation: 'fadeIn 0.5s ease' }}>
          <div>
            <h1 style={{ color: '#0f172a', fontSize: 26, fontWeight: 700, margin: 0 }}>Dashboard</h1>
            <p style={{ color: '#475569', fontSize: 14, marginTop: 6, marginBottom: 0 }}>Welcome back, {user.name} </p>
          </div>
          <div data-tour="month-picker" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button aria-label="Previous month" style={navBtn(false)} onClick={() => setMonth((m) => shiftMonth(m, -1))}>‹</button>
            <select value={month} onChange={(e) => setMonth(e.target.value)}
              style={{ background: '#ffffff', border: '1.5px solid #94a3b8', borderRadius: 10, padding: '8px 12px', color: '#0f172a', fontSize: 15, fontWeight: 600, fontFamily: 'inherit' }}>
              {!monthOptions.includes(month) && <option value={month}>{monthLabel(month)}</option>}
              {monthOptions.map((m) => <option key={m} value={m} style={{ background: '#0f172a' }}>{monthLabel(m)}{m === thisMonth ? ' (this month)' : ''}</option>)}
            </select>
            <button aria-label="Next month" style={navBtn(month >= thisMonth)} disabled={month >= thisMonth} onClick={() => setMonth((m) => shiftMonth(m, 1))}>›</button>
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 10, padding: '14px 18px', color: '#f87171', marginBottom: 24 }}>{error}</div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', border: '3px solid rgba(56,189,248,0.15)', borderTop: '3px solid #38bdf8', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
            <p style={{ color: '#475569' }}>Loading dashboard...</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 20, animation: 'fadeIn 0.5s ease' }}>

            {/* Payments that need attention (admins) */}
            {isAdmin && <div data-tour="unfulfilled"><UnfulfilledPanel headers={headers} /></div>}

            {/* Headline: transacted in the selected month */}
            <div data-tour="headline" style={{ ...panel, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, opacity: monthLoading ? 0.6 : 1 }}>
              <div>
                <div style={{ fontSize: 12.5, color: '#cbd5e1', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>
                  Total transacted · {monthLabel(month)}
                </div>
                <div style={{ fontSize: 40, fontWeight: 800, color: '#e2e8f0', lineHeight: 1 }}>{kes(transacted?.total)}</div>
                <div style={{ color: '#cbd5e1', fontSize: 14.5, marginTop: 8 }}>
                  {transacted?.count ?? 0} completed payment{transacted?.count === 1 ? '' : 's'}
                  {!isAdmin && ' · across your sites and accounts'}
                </div>
              </div>
              {ytd && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12.5, color: '#cbd5e1', letterSpacing: 1.2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>
                    {ytd.year} to date
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>{kes(ytd.total)}</div>
                </div>
              )}
            </div>

            {/* Breakdown by tenant and by account */}
            <div data-tour="breakdowns" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 20, opacity: monthLoading ? 0.6 : 1 }}>
              <RankedBars title={`By tenant · ${monthLabel(month, 'short')}`} rows={transacted?.byTenant || []}
                emptyText={isAdmin ? 'No tenants yet' : 'You are not assigned to any tenant sites'} />
              <RankedBars title={`By account · ${monthLabel(month, 'short')}`} rows={transacted?.byAccount || []}
                emptyText={isAdmin ? 'No accounts yet' : 'No accounts are shared with you'} />
            </div>

            {/* Year to date — always shown; click a month to select it */}
            {ytd && (
              <div data-tour="ytd" style={panel}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
                  <h3 style={{ color: '#ffffff', fontSize: 19, fontWeight: 700, margin: 0 }}>
                    Total transacted {isAdmin ? 'on the system' : ''} · {ytd.year} to date
                  </h3>
                  <span style={{ color: '#cbd5e1', fontSize: 13.5 }}>Click a month to view its breakdown</span>
                </div>
                <div style={{ color: '#cbd5e1', fontSize: 14.5, marginBottom: 16 }}>{kes(ytd.total)} from {ytd.count} payments</div>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={ytd.months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                    onClick={(e) => e?.activePayload?.[0] && setMonth(e.activePayload[0].payload.month)}>
                    <CartesianGrid vertical={false} stroke="rgba(148,163,184,0.25)" />
                    <XAxis dataKey="month" tickFormatter={(m) => monthLabel(m, 'short')} tick={{ fill: '#f1f5f9', fontSize: 14, fontWeight: 600 }} axisLine={{ stroke: 'rgba(148,163,184,0.2)' }} tickLine={false} />
                    <YAxis tickFormatter={compact} tick={{ fill: '#f1f5f9', fontSize: 14, fontWeight: 600 }} axisLine={false} tickLine={false} width={44} />
                    <Tooltip content={<YtdTooltip />} cursor={{ fill: 'rgba(56,189,248,0.06)' }} />
                    <Bar dataKey="total" maxBarSize={24} radius={[4, 4, 0, 0]} style={{ cursor: 'pointer' }}>
                      {ytd.months.map((m) => (
                        <Cell key={m.month} fill={BAR} fillOpacity={m.month === month ? 1 : 0.6} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Meter stats */}
            {stats && (
              <div data-tour="meter-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))', gap: 20 }}>
                <StatCard title="Total Meters" value={stats.totalMeters} icon="" color="#38bdf8" description={isAdmin ? 'All meters in the system' : 'Meters you can see'} />
                <StatCard title="Meters in Use" value={stats.metersInUse} icon="" color="#10b981" description="Currently active meters" />
                <StatCard title="Meters Not in Use" value={stats.metersNotInUse} icon="" color="#f87171" description="Inactive or unassigned meters" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
